import React from "react";
import { saveAs } from "file-saver";

import { orderBy, uniq } from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import { AppStore } from "../../../rootReducer";
import { getAreaCoordinatorsShiftIds, getShiftById, getShiftsByIds } from "../../Shifts/shiftSelector";
import { pdf, Image, Page, Text, View, Document, StyleSheet, Rect, Font } from "@react-pdf/renderer";
import { Shift } from "../../Shifts/types";
import { getLocations } from "../../Locations/locationSelector";
import { RawShiftData } from "../../Registration/CollectionSites/types";
import { formatTimeAs12Hour } from "domains/Registration/CollectionSites/utils";
import { AreaCoordinatorDay, ShiftPerson } from "domains/API/type";
import { useAppSelector } from "hooks";
import { isUserAdmin } from "domains/Person/personSelector";
import { Maybe } from "types";

Font.register({
  family: "Gibson",
  fonts: [
    { src: "https://db.onlinewebfonts.com/t/af83729ef342708b89deb4fbe42a865d.ttf" },
    { src: "https://db.onlinewebfonts.com/t/ad9c21e32d5c60a721d11912ca6a9291.ttf", fontWeight: 600 },
  ],
});

const styles = StyleSheet.create({
  body: {
    paddingTop: 30,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 40,
    fontFamily: "Gibson",
    fontSize: 11,
  },
  table: {
    // display: "table",
    width: "auto",
    borderStyle: "solid",
    borderColor: "#0f0f0e",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
  },
  tableColHeader: {
    width: "33.33%",
    borderStyle: "solid",
    borderColor: "#0f0f0e",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    verticalAlign: "middle",
  },
  tableCol: {
    width: "33.33%",
    borderStyle: "solid",
    borderColor: "#0f0f0e",
    borderWidth: 1,
    paddingLeft: 6,
    paddingRight: 6,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHeader: {
    fontWeight: 500,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 6,
    paddingRight: 6,
    verticalAlign: "middle",
  },
  header: {
    fontWeight: "bold",
    paddingBottom: 10,
  },
  tableCell: {},
  image: {
    height: 65,
    width: "auto",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    textAlign: "left",
  },
});

interface Props {
  allocatedShifts: Shift[];
  locations: { [id: string]: RawShiftData };
  state: AppStore;
  userIsAdmin: Maybe<boolean>;
}

const generateLocationString = (location: RawShiftData) => {
  const locationString = [];

  if (location?.region) {
    locationString.push(`${location.region}`);
  }
  if (location?.subRegion) {
    locationString.push(`${location.subRegion}`);
  }
  if (location?.area) {
    locationString.push(`${location.area}`);
  }
  return locationString.join(", ");
};

interface ShiftDay {
  displayDay: string;
  weekday: AreaCoordinatorDay;
}

const ShiftDetailsDoc = ({ allocatedShifts, locations, state, userIsAdmin }: Props) => {
  const shiftLocationIds = uniq(allocatedShifts.map((shift) => shift.locationId));
  return (
    <Document>
      <Page style={styles.body} wrap size="A4">
        {shiftLocationIds.map((shiftLocationId) => {
          const location = locations[shiftLocationId];
          const currentShifts = allocatedShifts.filter((shift) => shift.locationId === shiftLocationId);
          const orderedShifts = orderBy(currentShifts, "timeStamp");
          const days = uniq(
            orderedShifts
              .filter((currentShift) => {
                const storeShift = getShiftById(state, currentShift?.shiftId);
                const day: any = moment(currentShift.timeStamp).format("dddd");
                const shiftNotAvailable =
                  !userIsAdmin && !storeShift.days.includes(day) && !storeShift.days.includes("Both");

                return !shiftNotAvailable;
              })
              .map((currentShift) => moment(currentShift.timeStamp).format("dddd DD MMMM, YYYY")),
          );

          return days.map((day: string, index) => {
            const filteredShifts = orderedShifts.filter(
              (shift) => moment(shift.timeStamp).format("dddd DD MMMM, YYYY") === day,
            );

            return (
              <View
                break={index > 0}
                style={{
                  marginBottom: 30,
                }}
              >
                <View
                  style={{
                    marginBottom: 24,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <View>
                    <Text
                      style={{ ...styles.header, marginTop: 15 }}
                    >{`Pink Ribbon Street Appeal Roster - ${day}`}</Text>
                    <Text style={styles.header}>{generateLocationString(location)}</Text>
                    <Text style={styles.header}>{location?.collectionSite}</Text>
                    <Text style={styles.tableCell}>Location notes: {location?.siteNotes}</Text>
                  </View>
                  <Image
                    style={styles.image}
                    src={`data:image/png;base64,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`}
                  />
                </View>
                <View style={styles.table}>
                  <View style={styles.tableRow}>
                    <View style={styles.tableColHeader}></View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>Volunteer 1</Text>
                    </View>
                    <View style={styles.tableColHeader}>
                      <Text style={styles.tableCellHeader}>Volunteer 2</Text>
                    </View>
                  </View>
                  {filteredShifts.map((shift, index) => {
                    const unProcessedShiftPeople = Object.values(shift.people);
                    //Add an the person twice if the count is greater than 1
                    const shiftPeople = unProcessedShiftPeople.reduce<ShiftPerson[]>((accum, shiftPerson) => {
                      return [...accum, ...Array.from(Array(shiftPerson.personCount)).map((i) => shiftPerson)];
                    }, []);

                    return (
                      <View style={styles.tableRow} key={index}>
                        <View style={styles.tableCol}>
                          <View>
                            <View style={{ ...styles.tableCell, paddingTop: 16, paddingBottom: 2 }}>
                              <Text
                                style={{
                                  fontWeight: "bold",
                                }}
                              >
                                {`${formatTimeAs12Hour(shift.timeSlotStart)} - ${formatTimeAs12Hour(
                                  shift.timeSlotEnd,
                                )}`}
                              </Text>
                            </View>
                            <View style={{ ...styles.tableCell, paddingBottom: 16 }}>
                              <Text>{location.collectionSite}</Text>
                            </View>
                          </View>
                        </View>
                        <View style={styles.tableCol}>
                          <View style={styles.tableCell}>
                            <View>
                              <View style={{ paddingTop: 16 }}>
                                <Text>
                                  {shiftPeople[0] && `${shiftPeople[0]?.firstName} ${shiftPeople[0]?.lastName}`}
                                </Text>
                              </View>
                              <View>
                                <Text>{shiftPeople[0]?.mobile}</Text>
                              </View>
                              <View style={{ paddingBottom: 16 }}>
                                <Text>{shiftPeople[0]?.email}</Text>
                              </View>
                            </View>
                          </View>
                        </View>
                        <View style={styles.tableCol}>
                          <View style={styles.tableCell}>
                            <View>
                              <View style={{ paddingTop: 16 }}>
                                <Text>
                                  {shiftPeople[1] && `${shiftPeople[1]?.firstName} ${shiftPeople[1]?.lastName}`}
                                </Text>
                              </View>
                              <View>
                                <Text>{shiftPeople[1]?.mobile}</Text>
                              </View>
                              <View style={{ paddingBottom: 16 }}>
                                <Text>{shiftPeople[1]?.email}</Text>
                              </View>
                            </View>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          });
        })}
        <Text fixed style={styles.footer}>
          Any questions? Call the Pink Ribbon Street Appeal team on 0508 105 105
        </Text>
      </Page>
    </Document>
  );
};

interface ACPdfGeneratorProps {
  loadingData: boolean;
}

const generatePdfDocument = async (
  allocatedShiftsFromStore: Shift[],
  locations: { locations: { [id: string]: RawShiftData } },
  state: AppStore,
  userIsAdmin: Maybe<boolean>,
  location: RawShiftData,
) => {
  const blob = await pdf(
    <ShiftDetailsDoc
      allocatedShifts={allocatedShiftsFromStore}
      locations={locations.locations}
      state={state}
      userIsAdmin={userIsAdmin}
    />,
  ).toBlob();
  saveAs(blob, `${location?.collectionSite} Roster`);
};

const ACPdfGenerator: React.FC<ACPdfGeneratorProps> = ({ loadingData }) => {
  const locations = useSelector(getLocations);
  const areaCoordinatorShiftIds = useSelector(getAreaCoordinatorsShiftIds);
  const allocatedShiftsFromStore = useSelector((state: AppStore) => getShiftsByIds(state, areaCoordinatorShiftIds));
  const shiftLocationIds = uniq(allocatedShiftsFromStore.map((shift) => shift.locationId));
  const location = locations.locations[shiftLocationIds[0]];
  const state = useSelector((state: AppStore) => state);
  const userIsAdmin = useAppSelector(isUserAdmin);
  return (
    <div className="text-right w-full text-flamingo">
      <button
        disabled={loadingData}
        onClick={() => generatePdfDocument(allocatedShiftsFromStore, locations, state, userIsAdmin, location)}
      >
        {loadingData ? "Loading..." : "Download Roster"}
      </button>
    </div>
  );
};

export default ACPdfGenerator;
